import React from "react";
import styled from "styled-components";
import { device } from "../../lib/breakpoints";
import { APP_NAME } from "../../helpers/constants";

/**
 * Non-interactive OpsChat preview: matches hero `HomeMain` demo styling
 * (MarketingLeoDemoChat / TenantChat patterns) for consistent marketing surfaces.
 *
 * @param {object} props
 * @param {"hero" | "card"} [props.layout="hero"] - `hero`: tall flex fill; `card`: bounded height + shadow for section embeds
 * @param {string} props.ariaLabel
 * @param {string} [props.headerName="Leo AI"]
 * @param {string} props.headerSubtitle
 * @param {Array<{ role: "user" | "assistant", sender?: string, time: string, text: React.ReactNode }>} props.messages
 * @param {string} [props.previewKicker]
 * @param {string} [props.previewBody]
 * @param {string} [props.disclaimer]
 */
export default function StaticOpsChatPreview({
  layout = "hero",
  ariaLabel,
  headerName = "Leo AI",
  headerSubtitle,
  messages,
  previewKicker = "Visual preview",
  previewBody = `Sample conversation only, not interactive. Scroll down to try the live Leo AI demo.`,
  disclaimer = `Powered by ${APP_NAME} · Leo AI`,
}) {
  return (
    <DemoContainer role="region" aria-label={ariaLabel} $layout={layout}>
      <DemoHeader>
        <DemoHeaderAvatarWrap>
          <DemoLeoAvatar src="/icons/leo/leo.png" alt="Leo AI" />
        </DemoHeaderAvatarWrap>
        <DemoHeaderInfo>
          <DemoHeaderName>{headerName}</DemoHeaderName>
          <DemoHeaderSub>{headerSubtitle}</DemoHeaderSub>
        </DemoHeaderInfo>
        <DemoOnlineDot aria-label="Online" />
      </DemoHeader>

      <DemoMessages>
        {messages.map((m, i) => {
          const isAI = m.role === "assistant";
          const sender =
            m.sender ?? (isAI ? "Leo AI" : "You");
          return (
            <ChatMessage key={i} $isAI={isAI}>
              {isAI ? (
                <BubbleAvatarWrap>
                  <BubbleAvatar src="/icons/leo/leo.png" alt="Leo AI" />
                </BubbleAvatarWrap>
              ) : null}
              <DemoBubbleCol $isAI={isAI}>
                <DemoBubbleHeader $isAI={isAI}>
                  <DemoBubbleSender>{sender}</DemoBubbleSender>
                  <DemoBubbleTime>{m.time}</DemoBubbleTime>
                </DemoBubbleHeader>
                <BubbleContent $isAI={isAI}>{m.text}</BubbleContent>
              </DemoBubbleCol>
            </ChatMessage>
          );
        })}
      </DemoMessages>

      <DemoPreviewChrome role="note">
        <DemoPreviewKicker>{previewKicker}</DemoPreviewKicker>
        <DemoPreviewBody>{previewBody}</DemoPreviewBody>
      </DemoPreviewChrome>

      <DemoDisclaimer>{disclaimer}</DemoDisclaimer>
    </DemoContainer>
  );
}

const DemoContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$layout",
})`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-align: left;
  font-family: var(--font-secondary, "Inter", sans-serif);

  ${(p) =>
    p.$layout === "card"
      ? `
    border-radius: 24px;
    max-height: min(440px, 72vh);
    min-height: 300px;
    box-shadow:
      0 12px 48px rgba(0, 0, 0, 0.12),
      0 4px 16px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.06);
  `
      : `
    @media ${device.mobile} {
      height: 540px;
      max-height: 540px;
    }
    @media (min-width: 768px) {
      flex: 1;
      min-height: 520px;
    }
  `}
`;

const DemoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(
    135deg,
    var(--color-primary, #2c5f2d) 0%,
    var(--color-primary-dark, #1e4d1e) 100%
  );
  border-bottom: 1px solid rgba(44, 95, 45, 0.1);
  box-shadow: 0 2px 8px rgba(44, 95, 45, 0.15);
  color: white;
  flex-shrink: 0;
`;

const DemoHeaderAvatarWrap = styled.div`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const DemoLeoAvatar = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
  filter: brightness(0) invert(1);
`;

const DemoHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const DemoHeaderName = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: white;
  font-family: var(--font-primary, "Poppins", sans-serif);
  line-height: 1.2;
`;

const DemoHeaderSub = styled.p`
  margin: 0;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.7);
  font-family: var(--font-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DemoOnlineDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #4ade80;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
`;

/** Aligned with components/home/MarketingLeoDemoChat.jsx (live demo). */
const DEMO_ASSISTANT_AVATAR_PX = 24;
const DEMO_ASSISTANT_GAP = "0.35rem";
const DEMO_BUBBLE_EDGE_INSET = `calc(${DEMO_ASSISTANT_AVATAR_PX}px + ${DEMO_ASSISTANT_GAP})`;
const DEMO_CHAT_GUTTER_X = "clamp(0.65rem, 2.8vw, 1rem)";
const DEMO_USER_LEFT_INSET = "clamp(0.75rem, 4vw, 1.2rem)";
const DEMO_AI_RIGHT_INSET = "clamp(0.75rem, 4vw, 1.2rem)";

const DemoMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem ${DEMO_CHAT_GUTTER_X};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
  text-align: left;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  min-height: 0;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(241, 241, 241, 0.5);
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-primary, #2c5f2d);
    border-radius: 3px;
  }
`;

const ChatMessage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$isAI",
})`
  display: flex;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  flex-direction: row;

  ${(p) =>
    p.$isAI
      ? `
    justify-content: flex-start;
    padding: 0 ${DEMO_AI_RIGHT_INSET} 0 0;
    gap: ${DEMO_ASSISTANT_GAP};
  `
      : `
    justify-content: flex-end;
    padding: 0 ${DEMO_BUBBLE_EDGE_INSET} 0 ${DEMO_USER_LEFT_INSET};
    gap: ${DEMO_ASSISTANT_GAP};

    @media ${device.mobile} {
      justify-content: flex-start;
      padding: 0 ${DEMO_AI_RIGHT_INSET} 0 ${DEMO_CHAT_GUTTER_X};
    }
  `}
`;

const BubbleAvatarWrap = styled.div`
  flex-shrink: 0;
`;

const BubbleAvatar = styled.img`
  width: ${DEMO_ASSISTANT_AVATAR_PX}px;
  height: ${DEMO_ASSISTANT_AVATAR_PX}px;
  border-radius: 50%;
  object-fit: contain;
  background: var(--color-primary, #2c5f2d);
  padding: 3px;
  filter: brightness(0) invert(1);
`;

const DemoBubbleCol = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$isAI",
})`
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${(p) =>
    p.$isAI
      ? `
    flex: 1;
    min-width: 0;
    max-width: 100%;
    align-items: flex-start;
  `
      : `
    flex: 0 1 auto;
    max-width: 100%;
    align-items: flex-end;

    @media ${device.mobile} {
      flex: 1;
      width: 100%;
      align-items: flex-start;
    }
  `}
`;

const DemoBubbleHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$isAI",
})`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.25rem;
  flex-direction: row;
  width: 100%;

  ${(p) =>
    p.$isAI ? `justify-content: flex-start;` : `justify-content: flex-end;`}

  @media ${device.mobile} {
    ${(p) => !p.$isAI && `justify-content: flex-start;`}
  }
`;

const DemoBubbleSender = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  font-family: var(--font-secondary);
`;

const DemoBubbleTime = styled.div`
  font-size: 0.67rem;
  color: #9ca3af;
  font-family: var(--font-secondary);
`;

const BubbleContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "$isAI",
})`
  padding: ${(p) => (p.$isAI ? "0.7rem 0.75rem" : "0.7rem 0.85rem")};
  border-radius: 12px;
  font-size: 0.9rem;
  line-height: 1.65;
  font-family: var(--font-secondary);
  white-space: pre-wrap;
  word-break: break-word;
  text-align: left;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;

  ${(p) =>
    p.$isAI
      ? `
    align-self: flex-start;
    background: rgba(44, 95, 45, 0.05);
    color: #1f2937;
    border: 1px solid rgba(44, 95, 45, 0.1);
    box-shadow: 0 2px 4px rgba(44, 95, 45, 0.08);
  `
      : `
    align-self: flex-end;
    background: var(--color-primary, #2c5f2d);
    color: white;
    border: 1px solid var(--color-primary, #2c5f2d);
    box-shadow: 0 2px 4px rgba(44, 95, 45, 0.2);

    @media ${device.mobile} {
      align-self: stretch;
      width: 100%;
    }
  `}
`;

const DemoPreviewChrome = styled.div`
  flex-shrink: 0;
  padding: 0.75rem ${DEMO_CHAT_GUTTER_X};
  border-top: 1px dashed rgba(100, 116, 139, 0.45);
  background: linear-gradient(
    180deg,
    rgba(241, 245, 249, 0.95) 0%,
    #e2e8f0 100%
  );
  text-align: center;
`;

const DemoPreviewKicker = styled.p`
  margin: 0 0 0.2rem 0;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
  font-family: var(--font-secondary, sans-serif);
`;

const DemoPreviewBody = styled.p`
  margin: 0;
  font-size: 0.72rem;
  line-height: 1.45;
  color: #475569;
  font-family: var(--font-secondary, sans-serif);
  max-width: 17rem;
  margin-left: auto;
  margin-right: auto;
`;

const DemoDisclaimer = styled.p`
  text-align: center;
  padding: 0.4rem ${DEMO_CHAT_GUTTER_X};
  font-size: 0.65rem;
  color: #475569;
  font-family: var(--font-secondary);
  margin: 0;
  flex-shrink: 0;
  border-top: 1px solid rgba(44, 95, 45, 0.08);
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
`;
